import {
  FEATURES,
  NAV_ANCHORS,
  featureByPathname,
  groupedNav,
} from './features';
import { tabsOf } from './navTabs';
import { FEATURE_COPY, GLOBAL_ACTIONS, type FeatureCopy } from './featureCopy';

/**
 * 模块功能地图的检索索引。
 *
 * 索引面必须等于「用户能看见的东西」，而不是「侧栏有什么」：
 * Registry 的 123 条 Feature 里只有 72 条登记了 `nav`（进侧栏），其余多是
 * 「被某页收成页内 Tab 的功能」（黑名单 / 呼叫路由 / 时段 / 技能组 / 样本回流 …）。
 * 只按 nav 建索引，这些功能在功能地图里就完全不存在——用户连准确名字都搜不到。
 * 故索引 = 锚点 + 侧栏功能 + 侧栏功能下的全部页内 Tab + 路由已确证可达的面板/别名。
 *
 * 非侧栏 Feature 不无条件纳入：其中一部分是重定向别名（`CANONICAL_REDIRECTS` 里各域的
 * analytics 路由都指向 /report）或未挂路由的登记项，纳进来会给出「点了跳到别处/原地不动」的结果。
 * 判定口径：路由必须已被锚点或侧栏功能占用（面板与别名都满足），动态路由（含 `:`）一律排除——
 * 没有 id 进不去。
 */

export type EntryKind = 'anchor' | 'page' | 'tab' | 'panel' | 'action';

export interface EntryExtra {
  /** 命中理由里显示的依据名（别名 / 说明 / 动作 / 分组 / 路由 / 旧地址 / 所属页面 / 权限码） */
  label: string;
  text: string;
}

export interface SearchEntry {
  /** 稳定 key（结果列表 key、最近访问存储键） */
  key: string;
  /** 主名：Tab 条目用 Tab 名（用户搜的就是这个词），页面条目用功能名 */
  name: string;
  /** 展示名：Tab 条目为「所属功能 · Tab 名」 */
  display: string;
  /** 归属面包屑（分区 · 分组） */
  breadcrumb: string;
  /** 可跳路由（Tab 带 ?tab=）；全局动作无路由，靠 hint 回答「在哪儿」 */
  route: string;
  kind: EntryKind;
  /** 是否出现在侧栏（排序加成：同样命中时侧栏入口优先） */
  inSidebar: boolean;
  /** 权限码（Tab 用自身的子码，缺省继承所属页面；null = 随域放行）——由消费方做可见性裁剪 */
  permission: string | string[] | null;
  /** 角色白名单（仅锚点有；给定则优先于 permission） */
  roles?: string[];
  /** 需绑定坐席档案才可见（仅锚点与其页面内的面板有；判据见 `access.ts::canSeeEntry`） */
  requiresAgent?: boolean;
  /** 一句话说明（来自 featureCopy） */
  desc?: string;
  /** 全局动作的「在哪儿」（kind=action 时给，点击不跳转、只回答位置） */
  hint?: string;
  extra: EntryExtra[];
}

/** 语料查询：功能条目按 Feature id、页内 Tab 按 `<featureId>#<tabKey>`（见 featureCopy.ts） */
function copyOf(idOrTab: string): FeatureCopy | undefined {
  return FEATURE_COPY[idOrTab];
}

/** 把语料（说明/别名/动作）并进检索字段 */
function withCopy(base: EntryExtra[], copy: FeatureCopy | undefined): EntryExtra[] {
  if (!copy) return base;
  const out = [...base];
  for (const k of copy.keywords ?? []) out.push({ label: '别名', text: k });
  if (copy.desc) out.push({ label: '说明', text: copy.desc });
  for (const a of copy.actions ?? []) out.push({ label: '动作', text: a });
  return out;
}

/** 由 Registry 构建检索索引（纯函数，便于验收脚本复算条数） */
export function buildSearchIndex(): SearchEntry[] {
  const out: SearchEntry[] = [];
  const sections = groupedNav();
  // 所有页内 Tab 的标题：与某条 Tab 同名的独立功能页不再重复出现（见步骤 3）
  const tabTitles = new Set(FEATURES.flatMap((f) => tabsOf(f.id).map((t) => t.title)));

  const pushTabs = (
    featureId: string,
    featureName: string,
    route: string,
    crumb: string,
    inSidebar: boolean,
    permission: string | string[] | null,
  ) => {
    const tabs = tabsOf(featureId);
    for (const t of tabs) {
      const copy = copyOf(`${featureId}#${t.key}`);
      out.push({
        key: `${featureId}#${t.key}`,
        name: t.title,
        display: `${featureName} · ${t.title}`,
        breadcrumb: crumb,
        route: `${route}?tab=${t.key}`,
        kind: 'tab',
        inSidebar,
        // 页签可自带子码（设置 Landing 的入口与子项权限码不同），否则继承所属页面
        permission: t.permission ?? permission,
        desc: copy?.desc,
        extra: withCopy(
          [
            { label: '所属页面', text: featureName },
            { label: '分组', text: crumb },
          ],
          copy,
        ),
      });
    }
  };

  // 1) 顶层锚点（仪表盘 / 坐席工作台 / 待办中心）
  for (const a of NAV_ANCHORS) {
    // 锚点页的页内 Tab 登记在「按 route 反查到的 Feature」名下（待办中心 → workflow.inbox），
    // 直接用锚点 id 会查不到 tab，故先按 route 反查
    const anchorFeature = featureByPathname(a.route);
    const copy = copyOf(anchorFeature?.id ?? a.id);
    out.push({
      key: `anchor:${a.route}`,
      name: a.title,
      display: a.title,
      breadcrumb: '顶层入口',
      route: a.route,
      kind: 'anchor',
      inSidebar: true,
      permission: a.permission,
      roles: a.roles,
      requiresAgent: a.requiresAgent,
      desc: copy?.desc,
      extra: withCopy([{ label: '路由', text: a.route }], copy),
    });
    pushTabs(anchorFeature?.id ?? a.id, a.title, a.route, '顶层入口', true, a.permission);
  }

  // 2) 侧栏功能 + 其页内 Tab
  for (const s of sections) {
    for (const g of s.groups) {
      const crumb = `${s.title} · ${g.title || s.title}`;
      for (const f of g.items) {
        const copy = copyOf(f.id);
        const extras: EntryExtra[] = [
          { label: '分组', text: crumb },
          { label: '路由', text: f.route },
        ];
        if (f.permission) {
          extras.push({ label: '权限码', text: Array.isArray(f.permission) ? f.permission.join(' / ') : f.permission });
        }
        for (const loc of f.legacyLocations ?? []) extras.push({ label: '旧地址', text: loc });
        out.push({
          key: `feature:${f.id}`,
          name: f.title,
          display: f.title,
          breadcrumb: crumb,
          route: f.route,
          kind: 'page',
          // 暂不上侧栏的条目（nav.hidden）仍要搜得到——用户知道名字就该能找到页面，
          // 只是侧栏没有入口；inSidebar=false 让「侧栏直达」类建议不再推销它。
          inSidebar: !f.nav?.hidden,
          permission: f.permission,
          desc: copy?.desc,
          extra: withCopy(extras, copy),
        });
        // 该页的页内 Tab 也是用户要找的功能（黑名单 / 呼叫路由 / 时段 / 技能组 / 样本回流 …）：
        // 它们多数没有独立 nav 登记，只按 nav 建索引就永远搜不到。
        // 页签的 `inSidebar` 随所属页面：页面上侧栏隐藏时它也不在侧栏，别拿到「侧栏优先」的排序加成。
        pushTabs(f.id, f.title, f.route, crumb, !f.nav?.hidden, f.permission);
      }
    }
  }

  // 3) 非侧栏但路由可达的条目：面板（route = 锚点/侧栏页）与别名（route 已被侧栏功能占用）
  for (const f of FEATURES) {
    if (f.nav) continue;
    if (f.route.includes(':')) continue;
    // 无独立落点的（未挂路由 / 重定向别名）不进索引；口径见 Feature.orphan 与索引门禁
    if (f.orphan) continue;
    // 与某个页内 Tab 同名 → 只留 Tab 那条（IA 认定的入口就是那个 Tab），避免同名两条
    if (tabTitles.has(f.title)) continue;
    const hostAnchor = NAV_ANCHORS.find((a) => a.route === f.route);
    const hostFeature =
      FEATURES.find((x) => x.nav && x.route === f.route) ??
      // 按 route 反查时要排掉自己，否则独立页会把自己的标题当"宿主页"（面包屑变成"X 内的面板"）
      (featureByPathname(f.route)?.id === f.id ? undefined : featureByPathname(f.route));
    // 宿主页 = 该功能挂在谁下面：锚点 / 侧栏功能（工作台面板）… 其余是独立页
    const hostName = hostAnchor?.title ?? hostFeature?.title ?? '';
    const copy = copyOf(f.id);
    out.push({
      key: `panel:${f.id}`,
      name: f.title,
      display: f.title,
      breadcrumb: hostName ? `${hostName} 内的面板` : '功能页（侧栏无入口，可从所在域进入）',
      route: f.route,
      kind: hostName ? 'panel' : 'page',
      inSidebar: false,
      // 面板可见性随宿主页：宿主是锚点则按锚点角色/账号能力，否则按宿主页权限码；独立页按自身权限码
      permission: hostAnchor ? null : hostFeature?.permission ?? f.permission,
      roles: hostAnchor?.roles,
      requiresAgent: hostAnchor?.requiresAgent,
      desc: copy?.desc,
      extra: withCopy(
        [
          ...(hostName ? [{ label: '所属页面', text: hostName }] : []),
          { label: '路由', text: f.route },
        ],
        copy,
      ),
    });
  }

  // 4) 全局动作（顶栏/底栏的动作，不是页面）：用户会来搜「在哪改密码」，命中后只回答位置
  for (const a of GLOBAL_ACTIONS) {
    out.push({
      key: a.id,
      name: a.title,
      display: a.title,
      breadcrumb: '全局动作',
      route: '',
      kind: 'action',
      inSidebar: false,
      permission: null,
      hint: a.where,
      extra: a.keywords.map((k) => ({ label: '别名', text: k })),
    });
  }

  return out;
}

/** 各字段命中得分：名称优先；其次别名（用户自己用的说法，命中它说明找对了）；再次说明/动作/归属 */
const SCORE = {
  nameExact: 100,
  namePrefix: 70,
  nameHas: 55,
  /** 旧地址/权限码这类技术值：只用来兜住英文与历史路径 */
  extraHas: 18,
  crumbHas: 22,
  /** 长句里切出的片段（未分词）：低于任何整体命中，只用来兜住「把号码加进黑名单」这类输入 */
  fragmentName: 20,
  fragmentExtra: 12,
} as const;

/** 语料字段的权重（label 即命中理由里显示的依据名） */
const EXTRA_SCORE: Record<string, number> = {
  别名: 42,
  动作: 32,
  说明: 26,
  分组: SCORE.crumbHas,
  所属页面: SCORE.crumbHas,
  路由: SCORE.extraHas,
  旧地址: SCORE.extraHas,
  权限码: SCORE.extraHas,
};

const extraScore = (label: string) => EXTRA_SCORE[label] ?? SCORE.extraHas;

export interface SearchHit {
  entry: SearchEntry;
  score: number;
  /** 命中理由（命中名称之外的字段时给出依据，用户才知道为什么它出现了） */
  reason: string;
  /** 名称中命中的片段（用于高亮） */
  marks: string[];
  /** 强匹配（名称/别名/动作）还是弱匹配（只是归属或说明里出现过）——界面据此分组，弱匹配默认收起 */
  strong: boolean;
}

const KIND_BONUS: Record<EntryKind, number> = { anchor: 6, page: 4, tab: 2, panel: 0, action: 0 };

/** 长句兜底：中文不用空格分词，用户会整句输入（「把号码加进黑名单」）。
 *  整串命中不了时取「最长的、命中某字段的片段」——片段越长，越接近用户真正想要的那个词。
 *  只认含中文的片段：两字拉丁片段（"op" 命中 open-platform 路径）纯噪声，
 *  两字中文片段则是实词（录音 / 记录 / 名单），正是要兜住的那类输入。 */
const hasCjk = (s: string) => /[^\x00-\x7F]/.test(s);

function longestFragment(
  token: string,
  fields: Array<{ text: string; score: number }>,
  minLen = 2,
): { frag: string; score: number } | null {
  for (let n = Math.min(token.length, 8); n >= minLen; n -= 1) {
    let best: { frag: string; score: number } | null = null;
    for (let i = 0; i + n <= token.length; i += 1) {
      const frag = token.slice(i, i + n);
      if (!hasCjk(frag)) continue;
      for (const f of fields) {
        if (f.text.toLowerCase().includes(frag) && (!best || frag.length > best.frag.length)) {
          best = { frag, score: f.score };
        }
      }
    }
    if (best) return best;
  }
  return null;
}

interface Scored {
  score: number;
  reason: string;
  marks: string[];
  /** 命中的词数（放宽为「或」匹配时用于排序：命中越多越靠前） */
  matched: number;
  /** 是否命中「名称 / 别名 / 动作」——这三类才是"找的就是它"；只命中归属与说明的是弱匹配 */
  strong: boolean;
}

/** 单个条目对一组词的打分。requireAll=false 时只累加命中的词（用于「与」匹配零结果后的放宽） */
function scoreEntry(e: SearchEntry, tokens: string[], requireAll: boolean): Scored | null {
  const lowerName = e.name.toLowerCase();
  let total = 0;
  let matchedCount = 0;
  let bestExtra: EntryExtra | null = null;
  let bestExtraScore = 0;
  let fragmentReason = '';
  let partialToken = '';
  /** 命中过名称/别名/动作（见 Scored.strong） */
  let strong = false;
  const marks: string[] = [];
  for (const tk of tokens) {
    if (lowerName === tk) {
      total += SCORE.nameExact;
      marks.push(e.name);
      matchedCount += 1;
      strong = true;
      continue;
    }
    const at = lowerName.indexOf(tk);
    if (at === 0) {
      total += SCORE.namePrefix;
      marks.push(e.name.slice(0, tk.length));
      matchedCount += 1;
      strong = true;
      continue;
    }
    if (at > 0) {
      total += SCORE.nameHas;
      marks.push(e.name.slice(at, at + tk.length));
      matchedCount += 1;
      strong = true;
      continue;
    }
    let matched: EntryExtra | null = null;
    let matchedScore = 0;
    for (const ex of e.extra) {
      if (ex.text.toLowerCase().includes(tk)) {
        const s = extraScore(ex.label);
        if (s > matchedScore) {
          matchedScore = s;
          matched = ex;
        }
      }
    }
    if (matched) {
      total += matchedScore;
      matchedCount += 1;
      // 别名与动作属于「用户就是在找它」；分组/说明/路由只是归属线索，算弱匹配
      if (matched.label === '别名' || matched.label === '动作') strong = true;
      if (matchedScore > bestExtraScore) {
        bestExtraScore = matchedScore;
        bestExtra = matched;
      }
      continue;
    }
    // 整串没命中：退回「最长可命中片段」（「录音下载」→「录音」；只在整词较长时才切，短词切片纯噪声）
    if (tk.length >= 4) {
      const fragName = longestFragment(tk, [{ text: e.name, score: SCORE.fragmentName }], 2);
      const fragExtra = longestFragment(
        tk,
        e.extra.map((ex) => ({ text: ex.text, score: SCORE.fragmentExtra })),
        2,
      );
      // 两字片段命中面很宽（「记录」能命中一堆页），压到最低档，只保证「不至于一条都没有」
      const fragScore = (s: number, len: number) => (len <= 2 ? s * 0.4 : s);
      if (fragName && (!fragExtra || fragName.frag.length >= fragExtra.frag.length)) {
        total += fragScore(fragName.score, fragName.frag.length);
        marks.push(fragName.frag);
        matchedCount += 1;
        strong = true; // 名称片段仍算「找的就是它」
        fragmentReason = `名称含「${fragName.frag}」`;
        continue;
      }
      if (fragExtra) {
        total += fragScore(fragExtra.score, fragExtra.frag.length);
        matchedCount += 1;
        fragmentReason = `归属含「${fragExtra.frag}」`;
        continue;
      }
    }
    if (requireAll) return null;
    partialToken = tk;
  }
  if (matchedCount === 0) return null;
  let score = total + KIND_BONUS[e.kind] + (e.inSidebar ? 5 : 0);
  // 同分时短名更可能是用户要的那个（黑名单 > 呼入设置 · 黑名单）
  score += Math.max(0, 8 - e.name.length) * 0.1;
  // 放宽模式：命中词数加权，让「两词都中」的排在只中一词的前面
  if (!requireAll) score += matchedCount * 15;
  const reason =
    marks.length > 0 && matchedCount === tokens.length
      ? '命中功能名'
      : bestExtra
        ? `命中${bestExtra.label}「${bestExtra.text}」`
        : partialToken
          ? `只命中「${partialToken}」`
          : fragmentReason;
  return { score, reason, marks: [...new Set(marks)], matched: matchedCount, strong };
}

/** 分词检索：先按「与」要求每个词都命中；一条都没有时放宽为「或」（命中越多越靠前），
 *  避免「号码 黑名单」这类多词输入直接给空结果。排序按命中字段权重 + 命中词数 + 条目类型。 */
export function searchFeatureIndex(entries: SearchEntry[], query: string): SearchHit[] {
  const tokens = tokenize(query);
  if (tokens.length === 0) return [];
  const collect = (requireAll: boolean): SearchHit[] => {
    const out: SearchHit[] = [];
    for (const e of entries) {
      const s = scoreEntry(e, tokens, requireAll);
      if (s) out.push({ entry: e, score: s.score, reason: s.reason, marks: s.marks, strong: s.strong });
    }
    return out.sort((a, b) => b.score - a.score || a.entry.name.length - b.entry.name.length);
  };
  const strict = collect(true);
  if (strict.length > 0) return strict;
  return tokens.length > 1 ? collect(false) : strict;
}

/** 零结果兜底：按字符二元组重合度挑最接近的 n 条。
 *  只认二元组、不退到单字——单字重合给的是「改密码 → 号码资源」这类噪声；
 *  宁可不给（界面上另有「从这些入口开始」，零结果不会是死路）。 */
export function suggestSimilar(entries: SearchEntry[], query: string, n = 3): SearchEntry[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const grams = (s: string) => {
    const set = new Set<string>();
    for (let i = 0; i < s.length - 1; i += 1) set.add(s.slice(i, i + 2));
    return set;
  };
  const qg = grams(q);
  return entries
    .map((e) => {
      const eg = grams(e.name.toLowerCase());
      let same = 0;
      qg.forEach((g) => {
        if (eg.has(g)) same += 1;
      });
      return { e, same };
    })
    .filter((x) => x.same > 0)
    .sort((a, b) => b.same - a.same || a.e.name.length - b.e.name.length)
    .slice(0, n)
    .map((x) => x.e);
}

/** 零结果时给的「从这些入口开始」：侧栏页面按导航顺序取前 n 条（兜底永远是死路就没有兜底） */
export function commonEntries(entries: SearchEntry[], n = 6): SearchEntry[] {
  return entries.filter((e) => e.kind === 'page' && e.inSidebar).slice(0, n);
}

/** 拆词：空格分隔；整串无空格时按整串匹配（保留「呼入设置」这类长词的精确性） */
export function tokenize(query: string): string[] {
  return query
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
}
